import { NMNResult } from '@sparks-notation/core'
import { SequenceSection } from '@sparks-notation/core/parser/sequence/types'
import { chordVoiceAsNoteChar } from '@sparks-notation/core/parser/sparse2des/chordVoices'
import { Frac } from '@sparks-notation/util/frac'
import { ControlData } from './ControlData'
import { NMNToneScheduler } from './scheduler/NMNToneScheduler'
import type { Linked2MusicArticle } from '@sparks-notation/core/parser/des2cols/types'

// 大调主音半音数 → MusicXML 五度圈数
const semisToFifths = [0, 7, 2, 9, 4, -1, 6, 1, 8, 3, 10, 5]

// MIDI 音级 → 音名 + 变化音（用升号表示）
//
// ⚠️ 这两张表必须**严格 12 项**且逐项对应。原来写成 13 项、在第 5 位多塞了一个
// `E`，于是从 F 起全部错位一格：F 导成 E、G 导成 F♯、B 导成 A♯、C 导成 B……
// 单声部听起来只是「调不对」，双声部往返时表现为「导入后音级全乱」，很难一眼归因。
const pitchNames = ['C', 'C', 'D', 'D', 'E', 'F', 'F', 'G', 'G', 'A', 'A', 'B']
const pitchAlters = [0, 1, 0, 1, 0, 0, 1, 0, 1, 0, 1, 0]

// 四分音符分割数。必须能整除 2^n（到 64 分音符）以及 3、5、7 —— 连音的分母
// 就是这些数。原来取 960（= 2^6×3×5），七连音的每个音是 137.14/960 拍，
// 只能取 137，往返几次就会漂。6720 = 960×7 把 7 也覆盖进来。
const DIVISIONS = 6720

type NoteEvent = {
	startQuarters: number
	lengthQuarters: number
	midi: number | undefined // undefined = 休止符
	tieStart: boolean
	tieStop: boolean
	// 歌词：lyrics[v] 为第 v 段歌词（verse）中与该音符对位的文本（可为一音多字词块）
	lyrics?: (string | undefined)[]
	/**
	 * 连音（NMN 里的 `T[x,y](...)`，内核用 decoration char='T' 的 range 表示）。
	 *
	 * actual/normal 直接对应 MusicXML 的 `<actual-notes>` / `<normal-notes>`：
	 * NMN 的 x/y 是「每个音乘多少」，MusicXML 的 actual:normal 是「几个占几个」，
	 * 两者互为倒数 —— 但 NMN 的 `total` 就是 actual、`extraNumber` 就是 normal，
	 * 所以这里只做搬运，不需要再做倒数换算。
	 */
	tuplet?: {
		actual: number
		normal: number
		/** 是否组内第一个音（写 <tuplet type="start"/>） */
		start: boolean
		/** 是否组内最后一个音（写 <tuplet type="stop"/>） */
		stop: boolean
	}
}

type MeasureData = {
	number: number
	events: NoteEvent[]
	attr: {
		fifths: number | undefined
		beats: [number, number] | undefined
		qpm: number | undefined
	}
}

function esc(text: string) {
	return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

// ⚠️ 下限必须是 0，不能是 1。这个函数既算**时值**也算**位置**，而位置 0（小节第一音）
// 是完全合法的；写成 Math.max(1, ...) 会把每个小节的第一个音挪到 1/960 拍处，
// 紧接着为了回到正轨再补一个 <backup>，导入端就会把这些微小的重叠读成两个声部。
function toDivisions(quarters: number) {
	return Math.max(0, Math.round(quarters * DIVISIONS))
}

/**
 * 名义时值 → MusicXML 的 `<type>`（附点时再补 `<dot/>`）。
 *
 * 连音音符必须带 type：`<duration>` 只是实际时长，别的软件靠 type 判断「这是
 * 八分音符的三连音」还是「一坨奇怪时值」。认不出来就返回空串（不写），
 * 让 reader 退回按 duration 理解。
 */
function typeXml(quarters: number): string {
	const table: [number, string, boolean][] = [
		[4, 'whole', false],
		[3, 'half', true],
		[2, 'half', false],
		[1.5, 'quarter', true],
		[1, 'quarter', false],
		[0.75, 'eighth', true],
		[0.5, 'eighth', false],
		[0.375, '16th', true],
		[0.25, '16th', false],
		[0.1875, '32nd', true],
		[0.125, '32nd', false],
		[0.0625, '64th', false],
	]
	let best = table[0]
	for(const row of table) {
		if(Math.abs(row[0] - quarters) < Math.abs(best[0] - quarters)) best = row
	}
	if(Math.abs(best[0] - quarters) > 0.01) return ''
	return `<type>${best[1]}</type>` + (best[2] ? '<dot/>' : '')
}

// 分数比较（分母均为正）：a <= b
function fracLE(ax: number, ay: number, bx: number, by: number) {
	return ax * by <= bx * ay
}

type LyricCharLite = {sx: number, sy: number, text: string}

/**
 * 取出当前小节中，与某音符（startPos, length）对位的歌词文本。
 * 落在 [start, start+len) 区间内的字符按序合并（melisma 一音多字）。
 */
function lyricForNote(chars: LyricCharLite[] | undefined, nx: number, ny: number, lx: number, ly: number): string | undefined {
		if(!chars || chars.length == 0) return undefined
		// 分数加法：end = startPos + length（{x,y} 是分子/分母，不能逐分量相加）
		const endX = nx * ly + lx * ny
		const endY = ny * ly
		let text = ''
		for(const c of chars) {
			// c.startPos >= note.start && c.startPos < note.end
			// 使用 <= 包含终止音的歌词字符（例如，边丢失问题就是因为按 < 而不是 <= 比较）
			if(fracLE(nx, ny, c.sx, c.sy) && fracLE(c.sx, c.sy, endX, endY) && !(c.sx * endY == endX * c.sy)) {
				text += c.text
			}
		}
		return text || undefined
	}

function noteXml(ev: NoteEvent, voice: number) {
	const dur = Math.max(1, toDivisions(ev.lengthQuarters))
	let inner = ''
	if(ev.midi === undefined) {
		inner += '<rest/>'
	} else {
		const midi = Math.round(ev.midi)
		const octave = Math.floor(midi / 12) - 1
		const pc = ((midi % 12) + 12) % 12
		inner += `<pitch><step>${pitchNames[pc]}</step>`
		if(pitchAlters[pc] != 0) {
			inner += `<alter>${pitchAlters[pc]}</alter>`
		}
		inner += `<octave>${octave}</octave></pitch>`
	}
	inner += `<duration>${dur}</duration>`
	if(ev.tieStop) {
		inner += '<tie type="stop"/>'
	}
	if(ev.tieStart) {
		inner += '<tie type="start"/>'
	}
	// ⚠️ 子元素顺序是 DTD 规定的：duration → tie → voice → notations → lyric。
	// 之前把 <lyric> 写在 <voice> 前面，MuseScore 能容忍，但严格校验器（以及
	// 我们自己这套「先读 voice 再读 lyric」的导入端）都不该依赖这种乱序。
	inner += `<voice>${voice}</voice>`
	if(ev.tuplet) {
		// 名义时值 = 实际时值 / 缩放比例，比例 = normal/actual
		const ratio = ev.tuplet.normal / ev.tuplet.actual
		inner += typeXml(ev.lengthQuarters / ratio)
		inner += `<time-modification><actual-notes>${ev.tuplet.actual}</actual-notes>`
		inner += `<normal-notes>${ev.tuplet.normal}</normal-notes></time-modification>`
	}
	if(ev.tieStart || ev.tieStop || ev.tuplet) {
		inner += '<notations>'
		if(ev.tieStop) {
			inner += '<tied type="stop"/>'
		}
		if(ev.tieStart) {
			inner += '<tied type="start"/>'
		}
		if(ev.tuplet?.start) {
			inner += '<tuplet type="start"/>'
		}
		if(ev.tuplet?.stop) {
			inner += '<tuplet type="stop"/>'
		}
		inner += '</notations>'
	}
	if(ev.lyrics && ev.midi !== undefined) {
		ev.lyrics.forEach((text, v) => {
			if(text) {
				inner += `<lyric number="${v + 1}"><text>${esc(text)}</text></lyric>`
			}
		})
	}
	return `<note>${inner}</note>`
}

/**
 * 将解析后的乐谱导出为 MusicXML（partwise 4.0）文本
 *
 * - 沿用试听播放的反复序列（iterations 下标 1 起；无数据时回退 0），时间轴跨迭代累计
 * - 声部 → <part>；节拍机声部跳过；鼓点字符（X/Y/Z）跳过
 * - 连音线（~）转为 tie；同起音高的和弦音用 <chord/> 表示
 */
export module MusicXmlExporter {
	export function exportMusicXml(result: NMNResult, controlData: ControlData, title: string, options?: {source?: string}): string {
		const { score, sequence } = result.sequenced

		// 收集每个声部的歌词行（按声部签名 hash 对齐；每篇文章独立收集）
		// articleLyrics[articleIdx][hash] = verse 字符列表（每项对应该文章的一个小节下标）
		const articleLyrics = new Map<number, Map<string, (LyricCharLite[] | undefined)[][]>>()
		score.articles.forEach((article, articleIdx) => {
			if(article.type != 'music') return
			const musicArticle = article as Linked2MusicArticle
			const byPart = new Map<string, (LyricCharLite[] | undefined)[][]>
			for(const part of musicArticle.parts) {
				// 多段歌词 → 多个 verse；带替代旋律（Ns:）的行小节对位不同，MVP 跳过
				const verses = part.lyricLines
					.filter(line => !line.notesSubstitute || line.notesSubstitute.length == 0)
					.map(line => (line.sections || []).map((sec): LyricCharLite[] | undefined => {
						if(sec.type != 'section' || !sec.chars) return undefined
						return sec.chars.map((c): LyricCharLite => ({sx: c.startPos.x, sy: c.startPos.y, text: c.text}))
					}))
				if(verses.length == 0) continue
				byPart.set(part.signature.hash, verses)
			}
			if(byPart.size > 0) {
				articleLyrics.set(articleIdx, byPart)
			}
		})

		// 收集每个声部的小节事件
		const partMeasures = new Map<string, MeasureData[]>()
		let measureNumber = 0

		score.articles.forEach((article, articleIdx) => {
			if(article.type != 'music') return
			const seqArticle = sequence[articleIdx]
			if(!seqArticle) return
			const iterations = seqArticle.iterations.length > 1
				? seqArticle.iterations.slice(1)
				: seqArticle.iterations
			for(const iteration of iterations) {
				for(const section of iteration.sections) {
					measureNumber += 1
					for(const hash in section.parts) {
						const part = section.parts[hash]
						if(part.section.type != 'section') continue
						const control = controlData[hash]?.control
						if(control?.type == 'beatMachine') continue
						let list = partMeasures.get(hash)
						if(!list) {
							list = []
							partMeasures.set(hash, list)
						}
						// ⚠️ 每声部必须有**自己的** MeasureData 实例。
						// 旧写法在本小节开头建一个对象、给所有声部 `list.push(同一个对象)`，
						// 于是两个声部的事件攒进同一个 events 数组 —— 导出的每个 <part>
						// 内容一模一样（都是两行音的并集），双声部谱导出即错。
						let measures = list.find(m => m.number == measureNumber)
						if(!measures) {
							measures = {
								number: measureNumber,
								events: [],
								attr: {
									fifths: undefined,
									beats: undefined,
									qpm: section.qpm > 0 ? section.qpm : undefined,
								},
							}
							list.push(measures)
						}
						const octave = control && control.type == 'part' ? control.octave * 12 : 0
						// 该声部在本文章各 verse 的歌词（按文章内小节下标索引）
						const verses = articleLyrics.get(articleIdx)?.get(hash)
						const sectionChars: (LyricCharLite[] | undefined)[] | undefined = verses?.map(v => v[section.index])
						let tiedPrev = false
						// 本小节的连音范围。内核把 `T[x,y](...)` 记成 decoration char='T' 的
						// range，level = 连音数（actual），extraNumber = 多连音的时值分子
						// （normal，缺省时等于 level-1，即「x 个占 x-1 个的位置」）。
						//
						// ⚠️ range.endPos 是**组内最后一个音的起点**，不是终点（看 NoteEater
						// 里那份 push）。所以「音符是否在组内」用 startPos ∈ [start, end]
						// 闭区间判断即可，拿 endPos 当「音符终点」去比会漏掉最后一个音。
						const tuplets = (part.section.decoration as any[])
							.filter(d => d.type == 'range' && d.char == 'T') as {
								startPos: {x: number, y: number}
								endPos: {x: number, y: number}
								level: number
								extraNumber?: number
							}[]
						const tupletOf = (startPos: {x: number, y: number}) => {
							for(const t of tuplets) {
								const inside = fracLE(t.startPos.x, t.startPos.y, startPos.x, startPos.y)
									&& fracLE(startPos.x, startPos.y, t.endPos.x, t.endPos.y)
								if(!inside) continue
								return {
									actual: t.level,
									normal: t.extraNumber ?? t.level - 1,
									start: startPos.x * t.startPos.y == t.startPos.x * startPos.y,
									stop: startPos.x * t.endPos.y == t.endPos.x * startPos.y,
								}
							}
							return undefined
						}
						for(const note of part.section.notes) {
							if(note.type != 'note' || note.voided) continue
							const startQuarters = Frac.toFloat(note.startPos)
							const lengthQuarters = Frac.toFloat(note.length)
							if(NMNToneScheduler.isTonicNote(note.char)) {
								const pitchValue = NMNToneScheduler.getPitchValue(note.char, part.props, octave)
								const lyricTexts: (string | undefined)[] | undefined = sectionChars?.map(chars =>
									lyricForNote(chars, note.startPos.x, note.startPos.y, note.length.x, note.length.y)
								)
								const hasLyric = lyricTexts?.some(t => t != undefined)
								const tieStart = note.suffix.includes('~')
								const tieStop = tiedPrev
								measures.events.push({
									startQuarters,
									lengthQuarters,
									midi: pitchValue + 60,
									tieStart,
									tieStop,
									lyrics: hasLyric ? lyricTexts : undefined,
									tuplet: tupletOf(note.startPos),
								})
								// 柱式和弦的其余音：同起点、同时值，靠下面的 `evPos == lastStart`
								// 自动写成 <chord/>（歌词只挂在主音上）。
								for(const voice of note.char.voices ?? []) {
									const voiceChar = chordVoiceAsNoteChar(voice)
									if(!NMNToneScheduler.isTonicNote(voiceChar)) continue
									measures.events.push({
										startQuarters,
										lengthQuarters,
										midi: NMNToneScheduler.getPitchValue(voiceChar, part.props, octave) + 60,
										tieStart,
										tieStop,
										tuplet: tupletOf(note.startPos),
									})
								}
								tiedPrev = tieStart
							} else if(note.char.char == '0') {
								measures.events.push({
									startQuarters,
									lengthQuarters,
									midi: undefined,
									tieStart: false,
									tieStop: false,
									tuplet: tupletOf(note.startPos),
								})
								tiedPrev = false
							} else {
								tiedPrev = false
							}
						}
					}
				}
			}
		})

		// 调号 / 拍号（从第一个含声部属性的 sections 数据回填）
		outer: for(const article of score.articles) {
			if(article.type != 'music') continue
			const seqArticle = sequence[score.articles.indexOf(article)]
			if(!seqArticle) continue
			const iterations = seqArticle.iterations.length > 1
				? seqArticle.iterations.slice(1)
				: seqArticle.iterations
			for(const iteration of iterations) {
				for(const section of iteration.sections) {
					for(const hash in section.parts) {
						const part = section.parts[hash]
						if(part.section.type != 'section') continue
						const base = part.props.base
						const beats = part.props.beats ?? section.beats
						if(base && !isNaN(base.value)) {
							const fifths = semisToFifths[((base.value % 12) + 12) % 12]
							for(const list of partMeasures.values()) {
								if(list[0]) {
									list[0].attr.fifths = fifths
								}
							}
						}
						if(beats) {
							for(const list of partMeasures.values()) {
								if(list[0]) {
									list[0].attr.beats = [beats.value.x, beats.value.y]
								}
							}
						}
						break outer
					}
				}
			}
		}

		// 补齐空小节：某声部若中途整小节没有音（或以休止符跳过），它的列表会缺项，
		// 各 <part> 小节数不一致 —— 导入端与第三方工具都会错位。这里统一补成全休止小节。
		const qpmByNumber = new Map<number, number>()
		for(const list of partMeasures.values()) {
			for(const m of list) {
				if(m.attr.qpm !== undefined) qpmByNumber.set(m.number, m.attr.qpm)
			}
		}
		const maxNumber = [...partMeasures.values()].reduce((mx, list) =>
			list.reduce((m2, m) => Math.max(m2, m.number), mx), 0)
		for(const list of partMeasures.values()) {
			list.sort((a, b) => a.number - b.number)
			const existing = new Set(list.map(m => m.number))
			for(let n = 1; n <= maxNumber; n++) {
				if(existing.has(n)) continue
				list.push({
					number: n,
					events: [],
					attr: {fifths: undefined, beats: undefined, qpm: qpmByNumber.get(n)},
				})
			}
			list.sort((a, b) => a.number - b.number)
		}

		// 声部名：源码里 `N["伴奏"]:` 这类带标题的声部就用它当 <part-name>，
		// 否则退回「声部 N」。MuseScore 等工具会把 part-name 显示在谱表左侧。
		const partLabel = (hash: string, idx: number) => {
			const sig = controlData[hash]?.signature
			const title = sig && sig.type == 'titled' ? sig.value.text : ''
			return title && title.trim() ? title.trim() : `声部 ${idx + 1}`
		}

		// 声部内音高的中位数 → 决定谱号。左手伴奏整体在 C4 以下，用低音谱号，
		// 否则整段被画到加线下面很难读。
		const partClef = (hash: string) => {
			const pitches: number[] = []
			for(const m of partMeasures.get(hash) ?? []) {
				for(const ev of m.events) {
					if(ev.midi !== undefined) pitches.push(ev.midi)
				}
			}
			if(pitches.length == 0) return '<clef><sign>G</sign><line>2</line></clef>'
			pitches.sort((a, b) => a - b)
			const median = pitches[Math.floor(pitches.length / 2)]
			return median < 59
				? '<clef><sign>F</sign><line>4</line></clef>'
				: '<clef><sign>G</sign><line>2</line></clef>'
		}

		// 渲染 XML
		const partIds = [...partMeasures.keys()]
		let xml = '<?xml version="1.0" encoding="UTF-8"?>\n'
		xml += '<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 4.0 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">\n'
		xml += '<score-partwise version="4.0">\n'
		xml += `<work><work-title>${esc(title)}</work-title></work>\n`
		// MusicXML 里唯一能承载水印的位置是 <identification>：<source> 记来源，
		// <miscellaneous> 里的 name=value 是给第三方工具读的任意字段。
		// DTD 要求 score-partwise 下 work → identification → part-list，插错位置
		// MuseScore / Finale 之类的严格校验器会直接判文件非法。
		if(options?.source) {
			xml += '<identification>\n'
			xml += `<source>${esc(options.source)}</source>\n`
			xml += `<miscellaneous>`
			xml += `<miscellaneous-field name="watermark">${esc(options.source)}</miscellaneous-field>`
			xml += `</miscellaneous>\n`
			xml += '</identification>\n'
		}
		xml += '<part-list>\n'
		partIds.forEach((hash, i) => {
			xml += `<score-part id="P${i + 1}"><part-name>${esc(partLabel(hash, i))}</part-name></score-part>\n`
		})
		xml += '</part-list>\n'

		partIds.forEach((hash, partIdx) => {
			const measures = partMeasures.get(hash)!
			const voice = partIdx + 1
			const clefXml = partClef(hash)
			xml += `<part id="P${partIdx + 1}">\n`
			let lastFifths: number | undefined
			let lastBeats: [number, number] | undefined
			let lastQpm: number | undefined
			for(const m of measures) {
				xml += `<measure number="${m.number}">\n`
				const needAttr = m.number == 1
					|| m.attr.fifths !== undefined && m.attr.fifths !== lastFifths
					|| m.attr.beats !== undefined && (lastBeats === undefined || m.attr.beats[0] != lastBeats[0] || m.attr.beats[1] != lastBeats[1])
				if(needAttr) {
					xml += '<attributes><divisions>' + DIVISIONS + '</divisions>'
					if(m.attr.fifths !== undefined) {
						xml += `<key><fifths>${m.attr.fifths}</fifths></key>`
						lastFifths = m.attr.fifths
					}
					if(m.attr.beats !== undefined) {
						xml += `<time><beats>${m.attr.beats[0]}</beats><beat-type>${m.attr.beats[1]}</beat-type></time>`
						lastBeats = m.attr.beats
					}
					if(m.number == 1) {
						xml += clefXml
					}
					xml += '</attributes>\n'
				}
				if(m.attr.qpm !== undefined && m.attr.qpm != lastQpm) {
					xml += `<direction placement="above"><direction-type><metronome><beat-unit>quarter</beat-unit><per-minute>${m.attr.qpm}</per-minute></metronome></direction-type><sound tempo="${m.attr.qpm}"/></direction>\n`
					lastQpm = m.attr.qpm
				}
				// 按（起音，音高）排序后顺序输出
				//
				// 一个 <part> 对应源码里的一行 N:；柱式和弦 `[135]` 会在这里展开成
				// 同起点、同时值的多个事件，靠下面的 `isChord` 写成 `<chord/>`。
				// ⚠️ 和弦音**不能**先发 <backup> 再发 <note><chord/>：backup 会把
				// 游标退回去，MuseScore 会把和弦音当成串行音符。必须直接写 chord 音。
				const events = [...m.events].sort((a, b) =>
					a.startQuarters - b.startQuarters || (a.midi ?? -1) - (b.midi ?? -1)
				)
				let pos = 0
				let lastStart = -1
				for(const ev of events) {
					const evPos = toDivisions(ev.startQuarters)
					const evLen = toDivisions(ev.lengthQuarters)
					const isChord = evPos == lastStart
					if(!isChord) {
						if(evPos > pos) {
							xml += `<forward><duration>${evPos - pos}</duration></forward>\n`
							pos = evPos
						} else if(evPos < pos) {
							xml += `<backup><duration>${pos - evPos}</duration></backup>\n`
							pos = evPos
						}
					}
					if(isChord) {
						xml += `<note><chord/>${noteXml(ev, voice).slice('<note>'.length)}`
					} else {
						xml += noteXml(ev, voice)
					}
					lastStart = evPos
					pos = Math.max(pos, evPos + evLen)
				}
				xml += '</measure>\n'
			}
			xml += '</part>\n'
		})
		xml += '</score-partwise>\n'
		return xml
	}
}
